import type React from "react";
import { useNavigate } from "react-router";

import type { QuestionRequest } from "@/api/types";
import { Button } from "@/components/Button/Button";
import { Mail01Icon } from "@/components/Icons/Mail01Icon";
import { Notice } from "@/components/Notice/Notice";
import { useSlug } from "@/hooks/useSlug";
import { aiEmailUpdateMutations } from "@/queries/ai-email-updates/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

interface AskAiModalSubscriptionFooterProps {
  questionVariables: QuestionRequest | undefined;
  isInputQuestionForResponse: boolean;
  hasResponse: boolean;
}

export function AskAiModalSubscriptionFooter({
  questionVariables,
  isInputQuestionForResponse,
  hasResponse,
}: AskAiModalSubscriptionFooterProps): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const navigate = useNavigate();

  const {
    mutate: postAiEmailUpdate,
    isPending: isPendingPostAiEmailUpdate,
    isSuccess: isSuccessPostAiEmailUpdate,
    variables: aiEmailUpdateVariables,
  } = aiEmailUpdateMutations.useSubscribeAiEmailUpdate();

  return (
    <div className="flex w-full flex-col border-t border-grey-100 pt-4 text-grey-700">
      {isSuccessPostAiEmailUpdate && (
        <Notice
          icon={<Mail01Icon />}
          customBody={
            <p>
              {aiEmailUpdateVariables?.timePeriod === "weekly"
                ? t("component.ask-ai-modal.get-updates.weekly.success")
                : t("component.ask-ai-modal.get-updates.monthly.success")}
            </p>
          }
          type="positive"
          styling="outlined"
          action={{
            label: t("component.ask-ai-modal.get-updates.success.link.manage-updates"),
            onClick: () =>
              navigate(
                routes.emailUnsubscribe.aiUpdateList({
                  slug,
                }),
              ),
          }}
        />
      )}
      {!isSuccessPostAiEmailUpdate && (
        <div className="flex w-full flex-wrap items-center gap-4">
          <div className="min-w-80 flex-1 text-caption">
            {hasResponse ? (
              <p>{t("component.ask-ai-modal.get-updates.title")}</p>
            ) : (
              <p className="flex flex-row items-center gap-2">
                <span className="text-body">📬</span>
                <span>{t("component.ask-ai-modal.get-updates.title-placeholder")}</span>
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              title={
                !isInputQuestionForResponse
                  ? t("component.ask-ai-modal.get-updates.btn.disabled-different-question")
                  : undefined
              }
              styling="secondary"
              icon={<Mail01Icon />}
              disabled={
                !isInputQuestionForResponse ||
                (isPendingPostAiEmailUpdate && aiEmailUpdateVariables?.timePeriod === "monthly")
              }
              isLoading={isPendingPostAiEmailUpdate && aiEmailUpdateVariables?.timePeriod === "weekly"}
              onClick={() => {
                if (!questionVariables?.question) {
                  return;
                }

                postAiEmailUpdate({
                  timePeriod: "weekly",
                  prompt: questionVariables?.question,
                });
              }}
            >
              {t("component.ask-ai-modal.get-updates.btn.weekly")}
            </Button>
            <Button
              title={
                !isInputQuestionForResponse
                  ? t("component.ask-ai-modal.get-updates.btn.disabled-different-question")
                  : undefined
              }
              styling="secondary"
              icon={<Mail01Icon />}
              disabled={
                !isInputQuestionForResponse ||
                (isPendingPostAiEmailUpdate && aiEmailUpdateVariables?.timePeriod === "weekly")
              }
              isLoading={isPendingPostAiEmailUpdate && aiEmailUpdateVariables?.timePeriod === "monthly"}
              onClick={() => {
                if (!questionVariables?.question) {
                  return;
                }

                postAiEmailUpdate({
                  timePeriod: "monthly",
                  prompt: questionVariables?.question,
                });
              }}
            >
              {t("component.ask-ai-modal.get-updates.btn.monthly")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
